import { usePortsGetList } from '@ais-ui/api';
import { PortList } from '@ais-ui/api-spec/portserver';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import Map from 'ol/Map';
import { buffer } from 'ol/extent';
import { fromLonLat } from 'ol/proj';

interface PortProviderProps {
  children: ReactNode;
}

interface PortContextType {
  ports: PortList[] | null;
  portsByLocode: globalThis.Map<string, PortList>;
  isLoading: boolean;
  error: unknown;
  dataUpdatedAt: number | null;
  zoomToPort: (
    port: PortList | { geometry?: any; position?: { coordinates: number[] } },
    map: Map,
  ) => void;
}

const PortContext = createContext<PortContextType | null>(null);

export const PortProvider = ({ children }: PortProviderProps) => {
  const {
    data: fetchedPorts,
    isLoading,
    error,
    dataUpdatedAt,
  } = usePortsGetList();
  const [ports, setPorts] = useState<PortList[] | null>(null);

  useEffect(() => {
    if (fetchedPorts && fetchedPorts.records) {
      setPorts(fetchedPorts.records);
    }
  }, [fetchedPorts]);

  const portsByLocode = useMemo(() => {
    const map = new globalThis.Map<string, PortList>();
    if (ports) {
      ports.forEach((port) => {
        if (port.locode) {
          map.set(port.locode, port);
        }
      });
    }
    return map;
  }, [ports]);

  const zoomToPort = useCallback(
    (
      port: PortList | { geometry?: any; position?: { coordinates: number[] } },
      map: Map,
    ) => {
      if (!map) {
        console.warn('Map is not available');
        return;
      }

      const view = map.getView();

      // Handle port with OpenLayers geometry (from PortActions)
      if ('geometry' in port && port.geometry) {
        const extent = port.geometry.getExtent();
        const extentBuffer = 100;
        const bufferedExtent = buffer(extent, extentBuffer);

        view.fit(bufferedExtent, {
          maxZoom: 12,
        });
        return;
      }

      // Handle port with position coordinates (from PortProvider data)
      if ('position' in port && port.position?.coordinates) {
        const [longitude, latitude] = port.position.coordinates;

        // Transform coordinates from longitude/latitude to map projection
        view.setCenter(fromLonLat([longitude, latitude]));
        view.setZoom(12);
        return;
      }

      console.warn(
        '❌ Port does not have valid geometry or position data:',
        port,
      );
    },
    [],
  );

  const contextValue: PortContextType = {
    ports,
    portsByLocode,
    isLoading,
    error,
    dataUpdatedAt: dataUpdatedAt ?? null,
    zoomToPort,
  };

  // Don't show loading spinner here to avoid circular dependency with ui-core
  // Let the consuming components handle loading states as needed
  return (
    <PortContext.Provider value={contextValue}>{children}</PortContext.Provider>
  );
};

export const usePorts = () => {
  const context = useContext(PortContext);
  if (!context) {
    throw new Error('usePorts must be used within a PortProvider');
  }
  return context;
};
